import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { AuthCoreService } from 'src/app/core/auth-core/auth-core.service';
import {
  CompanyLeaderboardPlayer,
  CompanyLeaderboardService,
} from './company-leaderboard.service';

@Component({
  selector: 'app-company-leaderboard',
  templateUrl: './company-leaderboard.component.html',
})
export class CompanyLeaderboardComponent implements OnInit, OnDestroy {
  private _unsubscribe$ = new Subject<void>();

  leaderboard: CompanyLeaderboardPlayer[] = [];
  searchTerm = '';
  isLoading = false;
  errorMessage = '';
  lastUpdated: Date | null = null;
  page = 1;
  limit = 100;
  totalCount = 0;

  constructor(private _leaderboardService: CompanyLeaderboardService) {}

  ngOnInit(): void {
    this.loadLeaderboard();
  }

  loadLeaderboard(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this._leaderboardService
      .getLeaderboard(
        this.companyId,
        this.page,
        this.limit,
        this.searchTerm.trim(),
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe({
        next: (response) => {
          this.leaderboard = response?.list || [];
          this.totalCount = response?.totalCount || 0;
          this.lastUpdated = new Date();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Failed to load leaderboard', error);
          this.errorMessage = 'Failed to load leaderboard.';
          this.leaderboard = [];
          this.totalCount = 0;
          this.isLoading = false;
        },
      });
  }

  onSearch(): void {
    this.page = 1;
    this.loadLeaderboard();
  }

  onSearchChange(term: string): void {
    if (!term.trim()) {
      this.page = 1;
      this.loadLeaderboard();
    }
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.page = 1;
    this.loadLeaderboard();
  }

  get totalPlayers(): number {
    return this.totalCount;
  }

  get totalGamesPlayed(): number {
    return this.leaderboard.reduce(
      (sum, player) => sum + (Number(player.gamesPlayed) || 0),
      0,
    );
  }

  get totalScore(): number {
    return this.leaderboard.reduce(
      (sum, player) => sum + (Number(player.totalScore) || 0),
      0,
    );
  }

  trackByLearnerId(_index: number, player: CompanyLeaderboardPlayer): string {
    return player.learnerId || player.email;
  }

  get totalPages(): number {
    return Math.max(Math.ceil(this.totalCount / this.limit), 1);
  }

  get currentPageStart(): number {
    if (!this.totalCount) {
      return 0;
    }

    return (this.page - 1) * this.limit + 1;
  }

  get currentPageEnd(): number {
    return Math.min(this.page * this.limit, this.totalCount);
  }

  get hasPreviousPage(): boolean {
    return this.page > 1;
  }

  get hasNextPage(): boolean {
    return this.page < this.totalPages;
  }

  previousPage(): void {
    if (!this.hasPreviousPage) {
      return;
    }

    this.page -= 1;
    this.loadLeaderboard();
  }

  nextPage(): void {
    if (!this.hasNextPage) {
      return;
    }

    this.page += 1;
    this.loadLeaderboard();
  }

  private get companyId(): string {
    return (
      AuthCoreService.companyUser?.company?._id || '6138735afa514e001269cb2c'
    );
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
